import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { KeycloakService } from 'keycloak-angular';
import { NumberStatsComponent } from '../../number-stats/number-stats.component';
import { LineChartComponent } from '../../../../../my-shared-lib/src/lib/chart-components/line-chart/line-chart.component';
import { ConfigService } from '../../product-health/services/config.service';

interface PeriodOption {
  year: number;
  half: number;
  label: string;
}

@Component({
  selector: 'app-survey-pm-rollup',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSnackBarModule, NumberStatsComponent, LineChartComponent],
  templateUrl: './survey-pm-rollup.component.html',
  styleUrl: './survey-pm-rollup.component.scss',
})
export class SurveyPmRollupComponent {
  @Input() config: any;
  @Input() icons: any;
  @Input() scoreConfig: any;
  @Output() viewAllResponses = new EventEmitter<any>();
  @Output() refreshRollup = new EventEmitter<void>();

  searchText = '';
  activeActionsKey = '';
  showPeriodMenu = false;
  activePeriodKey = '';
  selectedPeriodMode = '';
  selectedPeriods: PeriodOption[] = [];
  visiblePeriodYear: number | null = null;
  pendingAverageUpdate: { product: any; selectedPeriods: PeriodOption[] } | null = null;
  private closeActionsMenuTimer: ReturnType<typeof setTimeout> | null = null;
  private appliedAveragePeriods = new Map<string, string[]>();

  constructor(
    private readonly configService: ConfigService,
    private readonly snackBar: MatSnackBar,
    private readonly keycloakService: KeycloakService
  ) {}

  get products(): any[] { return this.config?.products || []; }
  get selectedProducts(): any[] {
    return this.products.filter((product) => product?.selected);
  }
  get selectedCount(): number {
    return (this.config?.selection?.baseSelectedCount || 0) + this.selectedProducts.length;
  }
  get filteredProducts(): any[] {
    const query = this.searchText.trim().toLowerCase();
    return this.products.filter(p => String(p.product || '').toLowerCase().includes(query));
  }
  get periodMode(): string { return this.selectedPeriodMode || this.config?.periodMenu?.defaultMode; }
  getPeriodYear(product?: any): number {
    return this.visiblePeriodYear ||
      this.getPendingPeriod(product)?.year ||
      this.getDefaultSelectedPeriod(product)?.year ||
      this.getAvailableYears(product)[0] || 0;
  }
  getAvailableYears(product?: any): number[] {
    return Array.from(new Set(this.getAllPeriodOptions(product).map((period) => period.year)))
      .sort((a, b) => a - b);
  }
  getPeriodOptions(product?: any): PeriodOption[] {
    const year = this.getPeriodYear(product);
    return this.getAllPeriodOptions(product).filter((period) => period.year === year);
  }
  trackByPeriod(_index: number, period: PeriodOption): string {
    return `H${period.half}${period.year}`;
  }
  selectedPeriodLabel(product?: any): string {
    return this.getPendingPeriods(product).map((period) => period.label).join(', ');
  }
  getGridPeriodLabel(product?: any): string {
    return this.getGridPeriod(product)?.label || '';
  }
  canGoToPreviousPeriodYear(product?: any): boolean {
    const years = this.getAvailableYears(product);
    return years.indexOf(this.getPeriodYear(product)) > 0;
  }
  canGoToNextPeriodYear(product?: any): boolean {
    const years = this.getAvailableYears(product);
    const currentIndex = years.indexOf(this.getPeriodYear(product));
    return currentIndex >= 0 && currentIndex < years.length - 1;
  }

  getIconGlyph(iconName?: string): string {
    const iconMap: Record<string, string> = {
      chevron_left: '‹',
      chevron_right: '›',
      expand_more: '▾',
      more_vert: '⋮',
      warning: '⚠',
      arrow_back: '←',
      arrow_forward: '→',
      close: '×',
      view_column: '▦',
      file_download: '⇩',
      publish: '⇪',
      calendar_month: '📅',
      edit: '✎',
      delete: '⌫',
      info: 'ℹ'
    };

    return iconMap[String(iconName || '')] || iconName || '';
  }

  trackByProp(index: number, item: any): string {
    return item?.prop || item?.product || item?.key || item?.label || item?.title || String(index);
  }
  scoreClass(value: number | null): string {
    if (value == null) return 'muted';
    return value >= 7 ? 'high' : value >= 5 ? 'mid' : 'low';
  }
  statusClass(value: string): string {
    return 'status-' + this.normalizeStatus(value).toLowerCase().replace(/[_\s]+/g, '-');
  }
  displayStatus(value: string): string {
    return this.normalizeStatus(value).replace(/_/g, ' ');
  }
  displayLabel(value: string): string {
    const text = String(value || '').replace(/_/g, ' ');
    if (!text || text === text.toUpperCase()) return text;
    return text.replace(/\b\w/g, (char) => char.toUpperCase());
  }
  isActionsMenuOpen(product: any): boolean {
    return this.activeActionsKey === this.productKey(product);
  }

  openActionsMenu(product: any): void {
    this.cancelCloseActionsMenu();
    this.activeActionsKey = this.productKey(product);
  }

  closeActionsMenu(delay = 150): void {
    this.cancelCloseActionsMenu();
    this.closeActionsMenuTimer = setTimeout(() => {
      this.activeActionsKey = '';
      this.closeActionsMenuTimer = null;
    }, delay);
  }

  cancelCloseActionsMenu(): void {
    if (!this.closeActionsMenuTimer) return;
    clearTimeout(this.closeActionsMenuTimer);
    this.closeActionsMenuTimer = null;
  }

  togglePeriodMenu(product?: any): void {
    this.activeActionsKey = '';
    const productKey = product ? this.productKey(product) : '';
    const shouldClose = this.showPeriodMenu && this.activePeriodKey === productKey;
    this.showPeriodMenu = !shouldClose;
    this.activePeriodKey = shouldClose ? '' : productKey;
    if (product && this.showPeriodMenu) {
      this.initializePeriodSelection(product);
      product.expanded = true;
      if (!product.activeTrendDimension) {
        product.activeTrendDimension = this.config?.defaultTrendDimension || 'TCO';
      }
    }
  }

  movePeriodYear(product: any, direction: number): void {
    const years = this.getAvailableYears(product);
    const currentIndex = years.indexOf(this.getPeriodYear(product));
    const nextYear = years[currentIndex + direction];
    if (!nextYear) return;

    this.visiblePeriodYear = nextYear;
  }

  handlePeriodChange(product: any, period: PeriodOption): void {
    const selected = this.getPendingPeriods(product);
    const alreadySelected = this.isSelectedPeriod(period, product);
    this.setPendingPeriods(product, alreadySelected
      ? selected.filter((item) => item.year !== period.year || item.half !== period.half)
      : [...selected, period]);
    this.visiblePeriodYear = period.year;
  }

  selectPeriodMode(product: any, mode: string): void {
    this.selectedPeriodMode = mode;
    if (mode.toLowerCase() !== 'preset') return;

    const gridPeriod = this.getGridPeriod(product);
    this.visiblePeriodYear = gridPeriod?.year ?? null;
    this.setPendingPeriods(product, gridPeriod ? [gridPeriod] : []);
  }

  isPeriodMenuOpen(product: any): boolean {
    return this.showPeriodMenu && this.activePeriodKey === this.productKey(product);
  }

  openAllResponses(product: any): void {
    const reviewPeriod = this.getGridPeriod(product);
    this.viewAllResponses.emit({
      ...product,
      period: reviewPeriod ? `H${reviewPeriod.half}${reviewPeriod.year}` : product?.period,
    });
  }

  applyAveragePeriod(product: any): void {
    const action = (this.config?.actionsMenu || []).find((item: any) => item?.action === 'period');
    if (!action?.api?.payload) {
      this.togglePeriodMenu(product);
      return;
    }

    const selectedPeriods = this.getPendingPeriods(product);
    const gridPeriod = this.getGridPeriod(product);
    if (!selectedPeriods.length || !selectedPeriods.every((period) =>
      this.isValidPeriod(period) && this.getAllPeriodOptions(product).some((available) =>
        available.year === period.year && available.half === period.half
      )) || !this.isValidPeriod(gridPeriod)) {
      this.snackBar.open('Select a valid time period.', 'Close', {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['snackbar-error'],
      });
      return;
    }

    this.pendingAverageUpdate = { product, selectedPeriods: [...selectedPeriods] };
  }

  cancelAveragePeriodUpdate(): void {
    this.pendingAverageUpdate = null;
  }

  confirmAveragePeriodUpdate(): void {
    const pendingUpdate = this.pendingAverageUpdate;
    if (!pendingUpdate) return;

    this.pendingAverageUpdate = null;
    this.submitAveragePeriodUpdate(pendingUpdate.product, pendingUpdate.selectedPeriods);
  }

  private submitAveragePeriodUpdate(product: any, selectedPeriods: PeriodOption[]): void {
    const action = (this.config?.actionsMenu || []).find((item: any) => item?.action === 'period');
    if (!action?.api?.payload) return;

    this.setPendingPeriods(product, selectedPeriods);
    const payload = this.buildActionPayload(action.api.payload, product, action);
    this.configService
      .customApi(action.api.url || '/spriced/platform', payload, action.api.method || 'post')
      .subscribe({
        next: (response: any) => {
          const updateResult = this.getReviewUpdateResult(response);
          if (response?.commitStatus !== 'COMMIT_SUCCESS') {
            const status = this.normalizeStatus(updateResult?.status);
            const message = status === 'ERROR' || status === 'NOT_FOUND'
              ? updateResult?.message || action.errorMessage || 'Failed to update average score period.'
              : action.errorMessage || 'Failed to update average score period.';
            this.snackBar.open(message, 'Close', {
              duration: 4000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['snackbar-error'],
            });
            return;
          }

          const appliedPeriods = selectedPeriods.map((period) => `H${period.half}${period.year}`);
          this.appliedAveragePeriods.set(this.averageSelectionKey(product), appliedPeriods);
          try {
            localStorage.setItem(this.averageSelectionStorageKey(product), JSON.stringify(appliedPeriods));
          } catch {
            // Keep the successful selection in memory if browser storage is unavailable.
          }
          product.avgFrom = [...appliedPeriods];
          this.refreshRollup.emit();
          this.showPeriodMenu = false;
          this.activePeriodKey = '';
          this.snackBar.open(action.successMessage || 'Average score period updated successfully.', 'Close', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['snackbar-success'],
          });
        },
        error: () => {
          this.snackBar.open(action.errorMessage || 'Failed to update average score period.', 'Close', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['snackbar-error'],
          });
        },
      });
  }

  toggleProductDetails(product: any): void {
    product.expanded = !product.expanded;

    if (product.expanded && !product.activeTrendDimension) {
      product.activeTrendDimension = this.config?.defaultTrendDimension || 'TCO';
    }

    if (product.expanded && !product.trendLoaded && !product.trendLoading) {
      this.loadProductTrend(product);
    }
  }

  loadDimensionTrend(product: any, dimension: any): void {
    product.activeTrendDimension = dimension?.label;
    this.loadProductTrend(product, this.config?.dimensionTrendAction || this.config?.trendAction, dimension);
  }

  isDimensionTrendActive(product: any, dimension: any): boolean {
    const activeDimension = product?.activeTrendDimension || this.config?.defaultTrendDimension || 'TCO';
    return String(activeDimension).trim().toLowerCase() === String(dimension?.label || '').trim().toLowerCase();
  }

  handleAction(action: any, product: any): void {
    if (action?.action === 'period') {
      this.togglePeriodMenu(product);
      return;
    }

    if (!action?.api?.payload) {
      this.activeActionsKey = '';
      return;
    }

    const payload = this.buildActionPayload(action.api.payload, product, action);
    this.configService
      .customApi(action.api.url || '/spriced/platform', payload, action.api.method || 'post')
      .subscribe({
        next: () => {
          product.status = this.normalizeStatus(action.successStatus || product.status);
          this.activeActionsKey = '';
          this.snackBar.open(action.successMessage || 'Action completed successfully.', 'Close', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['snackbar-success'],
          });
        },
        error: () => {
          this.snackBar.open(action.errorMessage || 'Action failed.', 'Close', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['snackbar-error'],
          });
        },
      });
  }

  private productKey(product: any): string {
    return String(product?.id || product?.key || product?.product || '');
  }

  private getReviewUpdateResult(response: any): any {
    const candidates = [
      'responseList.0.survey_history.0',
      'responseList.0.survey_history',
      'survey_history.0',
      'survey_history',
    ];

    for (const path of candidates) {
      const result = this.readPath(response, path);
      if (Array.isArray(result)) return result[0] ?? null;
      if (result && typeof result === 'object') return result;
    }

    return null;
  }

  private initializePeriodSelection(product: any): void {
    const defaultPeriod = this.getDefaultSelectedPeriod(product);
    const source = this.appliedAveragePeriods.get(this.averageSelectionKey(product)) ??
      product?.avgFrom ?? this.readStoredAveragePeriods(product) ?? product?.avgSource ?? product?.avg_source;
    const savedPeriods = (Array.isArray(source) ? source : [source])
      .map((item: any) => this.parsePeriodOption(item, product))
      .filter((period: PeriodOption | null): period is PeriodOption => Boolean(period))
      .filter((period: PeriodOption) => this.getAllPeriodOptions(product).some((available) =>
        available.year === period.year && available.half === period.half
      ));
    this.setPendingPeriods(product, savedPeriods.length ? savedPeriods : defaultPeriod ? [defaultPeriod] : []);
    this.visiblePeriodYear = this.getPendingPeriod(product)?.year || this.getAvailableYears(product)[0] || null;
    this.selectedPeriodMode = this.config?.periodMenu?.defaultMode || 'Period';
  }

  private getDefaultSelectedPeriod(product?: any): PeriodOption | null {
    return this.parsePeriodOption(product?.avgSource ?? product?.avg_source, product) || this.getGridPeriod(product);
  }

  private averageSelectionKey(product: any): string {
    const period = this.getGridPeriod(product);
    return JSON.stringify([this.getCurrentUser().email, this.productKey(product), period?.year, period?.half]);
  }

  private averageSelectionStorageKey(product: any): string {
    return `phi.survey.appliedAveragePeriods:${this.averageSelectionKey(product)}`;
  }

  private readStoredAveragePeriods(product: any): string[] | null {
    try {
      const stored = JSON.parse(localStorage.getItem(this.averageSelectionStorageKey(product)) || 'null');
      return Array.isArray(stored) && stored.length &&
        stored.every((period) => typeof period === 'string' && /^H[12]\d{4}$/.test(period))
        ? stored : null;
    } catch {
      return null;
    }
  }

  private getGridPeriod(product?: any): PeriodOption | null {
    return this.parsePeriodOption(product?.period, product) || this.createPeriodOption(
      this.config?.periodMenu?.year ?? product?.year,
      this.config?.periodMenu?.half ?? product?.half
    );
  }

  private getAllPeriodOptions(product?: any): PeriodOption[] {
    const configuredPeriods = this.getConfiguredPeriods(product)
      .map((period: any) => this.parsePeriodOption(period, product))
      .filter((period: PeriodOption | null): period is PeriodOption => Boolean(period));

    return configuredPeriods;
  }

  private getConfiguredPeriods(product?: any): any[] {
    if (Array.isArray(product?.availablePeriods) && product.availablePeriods.length) {
      return product.availablePeriods;
    }
    if (Array.isArray(this.config?.periodMenu?.availablePeriods) && this.config.periodMenu.availablePeriods.length) {
      return this.config.periodMenu.availablePeriods;
    }

    return [];
  }

  private parsePeriodOption(source: any, product?: any): PeriodOption | null {
    if (source === null || source === undefined || source === '') return null;

    if (typeof source === 'object') {
      return this.createPeriodOption(
        source.year ?? source.avgYear ?? source.currentYear ?? product?.year,
        source.half ?? source.periodHalf ?? source.avgPeriod ?? source.period ?? source.value ?? source.label
      );
    }

    const text = String(source).trim();
    const compactMatch = text.match(/^(H[12])\s*[- ]?\s*(\d{4})$/i);
    if (compactMatch) {
      return this.createPeriodOption(Number(compactMatch[2]), compactMatch[1]);
    }

    const yearHalfMatch = text.match(/^(\d{4})\s*[- ]?\s*(H[12])$/i);
    if (yearHalfMatch) {
      return this.createPeriodOption(Number(yearHalfMatch[1]), yearHalfMatch[2]);
    }

    const halfMatch = text.match(/^H\s*([12])$/i);
    if (halfMatch) {
      return this.createPeriodOption(product?.year, Number(halfMatch[1]));
    }

    return null;
  }

  private createPeriodOption(yearValue: any, halfValue: any): PeriodOption | null {
    const year = Number(yearValue);
    const half = this.normalizeHalf(halfValue);
    if (!Number.isFinite(year) || !this.isValidHalf(half)) return null;

    return {
      year,
      half,
      label: `H${half} ${year}`,
    };
  }

  private normalizeHalf(value: any): number {
    if (typeof value === 'number') return value;
    const text = String(value ?? '').trim().toUpperCase();
    const match = text.match(/^(?:H)?([12])$/);
    return match ? Number(match[1]) : Number.NaN;
  }

  private isValidHalf(value: any): boolean {
    return value === 1 || value === 2;
  }

  isSelectedPeriod(period: PeriodOption, product?: any): boolean {
    return this.getPendingPeriods(product).some((selected) =>
      selected.year === period.year && selected.half === period.half
    );
  }

  getPendingPeriod(product?: any): PeriodOption | null {
    return this.getPendingPeriods(product)[0] || null;
  }

  getPendingPeriods(product?: any): PeriodOption[] {
    return product?.__pendingPeriodSelections ?? (product ? [] : this.selectedPeriods);
  }

  private setPendingPeriods(product: any, periods: PeriodOption[]): void {
    this.selectedPeriods = [...new Map(periods.map((period) => [`H${period.half}${period.year}`, period])).values()];
    if (product) product.__pendingPeriodSelections = this.selectedPeriods;
  }

  private isValidPeriod(period: PeriodOption | null): period is PeriodOption {
    return Boolean(period && Number.isFinite(period.year) && this.isValidHalf(period.half));
  }

  private getAverageSourcePeriod(product?: any): { year: number | ''; period: string } {
    const source = product?.avgSource ?? product?.avg_source;
    const parsedSource = this.parsePeriodOption(source, product) ||
      this.createPeriodOption(product?.avgYear ?? product?.avg_year, product?.avgPeriod ?? product?.avg_period) ||
      this.createPeriodOption(product?.year, product?.half);

    return {
      year: parsedSource?.year ?? '',
      period: parsedSource ? `H${parsedSource.half}` : '',
    };
  }

  private normalizeStatus(value: string): string {
    return String(value || '').trim().replace(/\s+/g, '_').toUpperCase();
  }

  formatPeriodLabel(value: any): string {
    return this.parsePeriodOption(value)?.label || String(value || '');
  }

  private loadProductTrend(product: any, trendAction = this.config?.trendAction, dimension?: any): void {

    if (!trendAction?.payload) {
      product.trendLoaded = true;
      return;
    }

    product.trendLoading = true;
    const payload = this.buildActionPayload(trendAction.payload, product, trendAction, dimension);

    this.configService
      .customApi(trendAction.url || '/spriced/platform', payload, trendAction.method || 'post')
      .subscribe({
        next: (response: any) => {
          this.applyProductTrendResponse(product, response, trendAction);
          product.trendLoaded = true;
          product.trendLoading = false;
        },
        error: () => {
          product.trendLoading = false;
          this.snackBar.open(trendAction.errorMessage || 'Failed to load product trend.', 'Close', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['snackbar-error'],
          });
        },
      });
  }

  handleSelectionAction(action: any): void {
    if (!action?.api?.payload) return;

    const selectedProducts = this.selectedProducts;
    if (!selectedProducts.length) {
      this.snackBar.open(action.noSelectionMessage || 'Select at least one product.', 'Close', {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['snackbar-error'],
      });
      return;
    }

    const payload = this.buildActionPayload(action.api.payload, null, action);
    this.configService
      .customApi(action.api.url || '/spriced/platform', payload, action.api.method || 'post')
      .subscribe({
        next: () => {
          selectedProducts.forEach((product) => {
            product.status = this.normalizeStatus(action.successStatus || action.status || product.status);
            product.selected = false;
          });
          this.snackBar.open(action.successMessage || 'Selected products updated successfully.', 'Close', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['snackbar-success'],
          });
        },
        error: () => {
          this.snackBar.open(action.errorMessage || 'Failed to update selected products.', 'Close', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['snackbar-error'],
          });
        },
      });
  }

  private applyProductTrendResponse(product: any, response: any, trendAction: any): void {
    const detail = this.extractTrendDetail(response, trendAction);
    if (detail === null || detail === undefined) return;

    const source = Array.isArray(detail) ? { trend: detail } : detail;
    const dimensionAverages = this.readFirstPath(source, trendAction.dimensionAveragesPath || [
      'perDimensionAverages',
      'dimensionAverages',
      'dimension_averages',
    ]);
    const trend = this.readFirstPath(source, trendAction.trendPath || [
      'pillars',
      'trend',
      'trendData',
      'trend_data',
    ]);
    const metadata = this.readFirstPath(source, trendAction.metadataPath || ['metadata']);

    if (dimensionAverages) {
      product.dimensionAverages = this.resolveDimensionAverages(dimensionAverages);
    }

    if (trend) {
      product.trend = this.resolveRollupTrend(trend, product.activeTrendDimension, product);
    }

    if (metadata) {
      product.metadata = metadata;
    }
  }

  private extractTrendDetail(response: any, trendAction: any): any {
    const configured = this.readPath(response, trendAction?.responsePath || '');
    if (configured !== undefined && configured !== null) return configured;

    const candidates = [
      'responseList.0.survey_history.0',
      'responseList.0.survey_history',
      'responseList.0.survey_trend.0',
      'responseList.0.survey_trend',
      'responseList.0.responseData.0',
      'responseList.0.responseData',
      'responseData.0',
      'responseData',
      'data.responseData.0',
      'data.responseData',
    ];

    for (const path of candidates) {
      const value = this.readPath(response, path);
      if (value !== undefined && value !== null) return value;
    }

    return null;
  }

  private buildActionPayload(payloadTemplate: any, product: any, action: any, dimension?: any): any {
    const context = this.buildActionContext(product, action, dimension);
    return this.resolvePayloadTemplate(payloadTemplate ?? {}, context);
  }

  private buildActionContext(product: any, action: any, dimension?: any): any {
    const user = this.getCurrentUser();
    const selectedProducts = this.selectedProducts;
    const selectedProductIds = selectedProducts
      .map((item) => item?.product || item?.product_id || item?.id)
      .filter((value) => value !== undefined && value !== null && value !== '');
    const selectedPeriod = product ? (this.getPendingPeriod(product) || this.getDefaultSelectedPeriod(product)) : null;
    const avgSource = this.getAverageSourcePeriod(product);
    const period =
      product?.period ||
      action?.period ||
      selectedPeriod?.label ||
      this.config?.periodMenu?.defaultPeriod ||
      this.config?.periodMenu?.rangeLabel ||
      '';

    return {
      product,
      action,
      config: this.config,
      dimension,
      user,
      period,
      selectedPeriod,
      avgFrom: this.getPendingPeriods(product).map((period) => `H${period.half}${period.year}`),
      reviewPeriod: this.getGridPeriod(product)
        ? `H${this.getGridPeriod(product)!.half}${this.getGridPeriod(product)!.year}`
        : '',
      gridPeriod: this.getGridPeriod(product),
      avgSource,
      avgYear: avgSource.year,
      avgPeriod: avgSource.period,
      product_id: product?.product,
      selectedProducts: selectedProductIds,
      approvedBy: user.email,
    };
  }

  private resolvePayloadTemplate(template: any, context: any): any {
    if (Array.isArray(template)) {
      return template.map((item) => this.resolvePayloadTemplate(item, context));
    }

    if (template && typeof template === 'object') {
      return Object.entries(template).reduce((result: any, [key, value]) => {
        result[key] = this.resolvePayloadTemplate(value, context);
        return result;
      }, {});
    }

    if (typeof template !== 'string') {
      return template;
    }

    const exactToken = template.match(/^\{\{\s*([^}]+?)\s*\}\}$/);
    if (exactToken) {
      return this.resolveTemplateToken(context, exactToken[1].trim());
    }

    return template.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, token) =>
      String(this.resolveTemplateToken(context, String(token).trim()))
    );
  }

  private resolveTemplateToken(context: any, token: string): any {
    const [path, ...formatters] = token.split('|').map((part) => part.trim()).filter(Boolean);
    let value = this.readPath(context, path);

    formatters.forEach((formatter) => {
      if (formatter === 'uppercase') {
        value = String(value ?? '').toUpperCase();
      }
      if (formatter === 'lowercase') {
        value = String(value ?? '').toLowerCase();
      }
    });

    return value ?? '';
  }

  private getCurrentUser(): any {
    const token = this.keycloakService.getKeycloakInstance()?.idTokenParsed || {};
    return {
      email: token['email'] || token['preferred_username'] || '',
      name: token['name'] || '',
      username: token['preferred_username'] || '',
    };
  }

  private readPath(source: any, path: string): any {
    return String(path || '').split('.').reduce((current: any, key: string) => {
      if (current === undefined || current === null) return undefined;
      return current[key];
    }, source);
  }

  private readFirstPath(source: any, paths: string[] | string): any {
    const pathList = Array.isArray(paths) ? paths : [paths];

    for (const path of pathList) {
      const value = this.readPath(source, path);
      if (value !== undefined && value !== null && value !== '') return value;
    }

    return null;
  }

  private resolveDimensionAverages(source: any): any {
    if (Array.isArray(source)) return source;
    if (!source || typeof source !== 'object') return null;

    return Object.entries(source).map(([key, value]) => {
      const numeric = Number(value);
      return {
        label: key,
        value,
        width: Number.isFinite(numeric) ? Math.max(0, Math.min(100, numeric * 10)) : 0,
        color: Number.isFinite(numeric) && numeric >= 6 ? 'green' : 'yellow',
      };
    });
  }

  private resolveRollupTrend(source: any, dimensionLabel?: string, product?: any): any {
    if (!Array.isArray(source) || !source.length) return source;

    const baseTrend = this.config?.trend || {};
    const dimensionKey = String(
      dimensionLabel ||
      this.config?.defaultTrendDimension ||
      Object.keys(source.find((item) => item?.perDimensionAverages)?.perDimensionAverages || {})[0] ||
      ''
    );

    return {
      ...baseTrend,
      widgetId: this.getTrendWidgetId(product, dimensionKey),
      chartData: {
        ...(baseTrend.chartData || {}),
        category: source.map((item) => item?.label || item?.period),
        legend: [this.displayLabel(dimensionKey), 'Avg Score'],
        series: [
          {
            ...(baseTrend.chartData?.series?.[0] || {}),
            name: this.displayLabel(dimensionKey),
            type: 'bar',
            data: source.map((item) =>
              this.readDimensionValue(item?.perDimensionAverages, dimensionKey) ??
              item?.value ??
              null
            ),
          },
          {
            ...(baseTrend.chartData?.series?.[1] || {}),
            name: 'Avg Score',
            type: 'line',
            data: source.map((item) => item?.avgScore ?? null),
          },
        ],
      },
    };
  }

  private getTrendWidgetId(product: any, dimensionKey: string): string {
    const baseId = String(this.config?.trend?.widgetId || 'survey-audit-trends-overview');
    const productId = String(product?.id || product?.product || 'product')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    const dimensionId = String(dimensionKey || 'trend')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    return `${baseId}-${productId}-${dimensionId}`;
  }

  private readDimensionValue(source: any, dimensionKey: string): any {
    if (!source || typeof source !== 'object') return null;
    if (source[dimensionKey] !== undefined && source[dimensionKey] !== null) {
      return source[dimensionKey];
    }

    const normalizedKey = String(dimensionKey || '').trim().toLowerCase();
    const matchedKey = Object.keys(source).find(
      (key) => key.trim().toLowerCase() === normalizedKey
    );

    return matchedKey ? source[matchedKey] : null;
  }
}
